<script setup>

import {computed, ref} from "vue";
import {useStore} from "vuex";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import NoSubjects from '@/assets/gamification/no_subjects.png';
import {useRoute} from "vue-router";
import CraftingQuestionDialog from "@/components/gamification/question/CraftingQuestionDialog.vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()
const route = useRoute()

const props = defineProps(['isOpen','initialSelectedQuestions'])
const emit = defineEmits(['close','saveOptions'])

const selectedQuestions = ref([])
const search = ref('')
const loading = ref(false)
const loadingQuestions = ref(false)
const selectedSortOption = ref('newest')
const selectedSubject = ref(null)
const questionEdit = ref({showDialog: false, id: null})

const subjects = computed(()=> store.getters['Subjects/getSubjects'])
const questions = computed(()=> store.getters['Questions/fetchedQuestionsBySubjectId'])
const subjectId = computed(()=> Number(route.params.subjectId))
const goToTestFromSeminar = computed(()=> store.getters['Seminars/goToTestFromSeminar'])
const fromSubjectId = computed(()=> store.getters['Seminars/fromSubjectId'])
const sortedQuestions = computed(() => {
  const items = [...questions.value];
  if(selectedQuestions.value === 'newest'){
    return items
  } else {
    return items.sort((a, b) => {
      if (selectedSortOption.value === 'oldest') {
        return new Date(a.date) - new Date(b.date);
      }
      if (selectedSortOption.value === 'alpha') {
        return a.text.localeCompare(b.text, 'cs');
      }
      return 0;
    });
  }
})
const mappedQuestions = computed(()=>{
  return sortedQuestions.value.map(q => {
    return {
      id: q.id,
      text: q.text
    }
  })
})

async function load(){
  loading.value = true
  await store.dispatch('Subjects/fetchAllSubjects')
  const id = goToTestFromSeminar.value ? fromSubjectId.value : subjectId.value
  selectedSubject.value = store.getters['Subjects/getSubjectById'](id)
  await store.dispatch('Questions/fetchQuestionsBySubjectId', selectedSubject.value.id)
  selectedQuestions.value = []
  if(props.initialSelectedQuestions){
    selectedQuestions.value = props.initialSelectedQuestions.map(q => {
      return {
        id: q.id,
        text: q.text
      }
    })
  }
  loading.value = false
}

async function subjectChanged(subject){
  loadingQuestions.value = true
  selectedSubject.value = subject
  await store.dispatch('Questions/fetchQuestionsBySubjectId', subject.id)
  loadingQuestions.value = false
}

function openQuestionDialog(id){
  questionEdit.value.id = id
  questionEdit.value.showDialog = true
}

async function closeQuestionDialog(){
  questionEdit.value.showDialog = false
  loading.value = true
  await store.dispatch('Questions/fetchQuestionsBySubjectId', selectedSubject.value.id)
  selectedQuestions.value.forEach(sq => {
    const find = mappedQuestions.value.find(mq => mq.id === sq.id)
    if(find){
      sq.text = find.text
    }
  })
  loading.value = false
}

function save(){
  emit('saveOptions',{questions: [...selectedQuestions.value]})
}

const headers = [
  { key: 'text', title: t('gamification.question'), sortable: false, width: "90%" },
  { key: 'action', title: t('gamification.edit'), sortable: false, width: "10%" },
]
const sortOptions = [
  {label: t('gamification.newest'), value: 'newest'},
  {label: t('gamification.oldest'), value: 'oldest'},
  {label: t('gamification.alphabetical'), value: 'alpha'},
]
</script>

<template>
<v-dialog
    :model-value="props.isOpen"
    @update:model-value="value => !value && emit('close')"
    max-width="1000"
    @after-enter="load"
    height="80%"
>
  <crafting-question-dialog
      :question-id="questionEdit.id"
      :show-dialog="questionEdit.showDialog"
      :subject-id="selectedSubject?.id"
      @close="closeQuestionDialog"
  />
  <v-card>
    <v-card-title
        class="d-flex justify-space-between align-center py-2"
    >
      <div class="d-flex align-center ga-5">
        {{ $t('gamification.title_select_questions') }}
        <v-chip
            :color="selectedQuestions.length === 0 && 'grey-lighten-1' "
            label
        >
          {{ selectedQuestions.length }} {{ $t('gamification.selected') }}
        </v-chip>
      </div>
      <v-btn @click="$emit('close')" icon="close" variant="text" />
    </v-card-title>
    <v-card-item>
      <div v-if="loading" class="text-center">
        <v-progress-circular color="primary" indeterminate></v-progress-circular>
      </div>
      <div v-else>
        <div class="d-flex justify-space-between align-center ga-5">
          <div class="d-flex align-center ga-5 w-100">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" :disabled="loadingQuestions" color="grey-darken-1" icon="swap_vert" variant="text" />
              </template>
              <v-list color="primary">
                <v-list-item
                    v-for="option in sortOptions"
                    :key="option.value"
                    width="210"
                    link
                    activeColor="primary"
                    :active="selectedSortOption === option.value"
                    @click="selectedSortOption = option.value"
                >
                  {{ option.label }}
                </v-list-item>
              </v-list>
            </v-menu>
            <v-text-field v-model="search" :disabled="loadingQuestions" prepend-icon="search" :placeholder="$t('gamification.search_question')" color="primary" variant="underlined" />
          </div>
          <div class="w-100 pl-15">
            <v-select
                :model-value="selectedSubject"
                @update:model-value="subjectChanged"
                :items="subjects"
                item-title="name"
                return-object
                :label="$t('gamification.subject')"
                variant="underlined"
                :disabled="loadingQuestions"
                color="primary"
            />
          </div>
        </div>

        <v-data-table-virtual
            v-model="selectedQuestions"
            :items="mappedQuestions"
            :headers="headers"
            :search="search"
            show-select
            :loading="loadingQuestions"
            return-object
            loading-text="Načítání..."
        >
          <template #item.action="{ item }">
            <v-btn @click="openQuestionDialog(item.id)" color="grey-darken-3" variant="text" size="small" icon="edit" />
          </template>
          <template #no-data>
            <div
                class="w-100 d-flex flex-column justify-center align-center"
            >
              <img
                  style="width: 400px"
                  :src="NoSubjects"
                  alt="noQuestionsImage">
              <div class="text-h6">{{ $t('gamification.question_list_empty') }}</div>
              <div class="font-weight-light text-grey-darken-1"> {{ $t('gamification.can_create_new_question') }}</div>
              <PrimaryButton @click="openQuestionDialog(null)" class="mt-5 mb-5">
                {{$t('gamification.create_question')}}
              </PrimaryButton>
            </div>
          </template>
        </v-data-table-virtual>
      </div>
    </v-card-item>
    <v-spacer />
    <v-card-actions
        class="px-5 pb-5"
    >
      <v-btn
          v-if="questions.length > 0"
          :text="$t('gamification.add_another_question')"
          variant="text"
          rounded
          color="primary"
          @click="openQuestionDialog(null)"
      />
      <v-spacer />
      <v-btn
          :text="$t('gamification.save_selection')"
          :disabled="selectedQuestions.length === 0"
          color="primary"
          rounded
          variant="flat"
          @click="save"
      />
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<style scoped>

</style>